/* Reading a typed figure back into the string the API takes.
 *
 * The other half of `format.ts`. That module prints `22 000` with a U+202F
 * group separator, readers copy the printed shape back into a field, and a
 * Russian keyboard types the decimal as a comma — so `22 000`, `22 000,5` and
 * `22000.5` are one number and must reach the server as `22000.5`. A field that
 * accepts the shape locally and sends it raw is the failure this exists to end
 * (#134, N-13: `"weightKg":"22 000"` answered with a 422).
 *
 * `normaliseDecimal` lived in `features/finance/lines.ts` until the quotes
 * feature needed it too; it moved here unchanged, and that module re-exports it
 * so its existing callers keep their import.
 *
 * The value stays a STRING throughout (AD-9). Number() would turn 22.4 into
 * 22.399999999999999 on its way to an offer; these functions only move
 * separators and never parse.
 */

/** Strips every space — ASCII, U+00A0 and the U+202F `formatNumber` groups
 *  with (`\s` covers both no-break spaces in JavaScript) — and turns ONE comma
 *  into the decimal dot.
 *
 *  Only the first comma moves: `22,000.5` is ambiguous (a comma group or a
 *  comma decimal) and is left malformed for `isDecimal` to refuse rather than
 *  guessed into a number nobody typed. */
export function normaliseDecimal(raw: string): string {
  return raw.replace(/\s/g, '').replace(',', '.')
}

/** A non-negative decimal in any of the accepted shapes. Blank is NOT a
 *  decimal — a caller with an optional field checks for blank itself, so the
 *  "may be empty" decision stays visible at the field. */
export function isDecimal(raw: string): boolean {
  return /^\d+(\.\d+)?$/.test(normaliseDecimal(raw))
}

/** A non-negative integer, group separators allowed: `1 000` is a count. */
export function isWholeNumber(raw: string): boolean {
  return /^\d+$/.test(normaliseDecimal(raw))
}
